/**
 * Mancala Error Handler
 *
 * Wraps ErrorHandlingService for game-specific error classification.
 * All user-facing messages go through NotificationService — never raw alert().
 *
 * Follows ReversiErrorHandler pattern.
 */

import {
  ErrorHandlingService,
  ErrorType,
  ErrorSeverity,
} from '../../services/ErrorHandlingService';
import {
  NotificationService,
  NotificationType,
} from '../../services/NotificationService';

export enum MancalaErrorType {
  INVALID_MOVE = 'INVALID_MOVE',
  NOT_YOUR_TURN = 'NOT_YOUR_TURN',
  GAME_ENDED = 'GAME_ENDED',
  GAME_LOGIC = 'GAME_LOGIC',
  CONNECTION_ERROR = 'CONNECTION_ERROR',
  CORRUPTED_DATA = 'CORRUPTED_DATA',
  AI_TIMEOUT = 'AI_TIMEOUT',
  MULTIPLAYER_VALIDATION = 'MULTIPLAYER_VALIDATION',
  SAVE_ERROR = 'SAVE_ERROR',
  LOAD_ERROR = 'LOAD_ERROR',
  UNKNOWN = 'UNKNOWN',
}

export interface MancalaError {
  type: MancalaErrorType;
  message: string;
  details?: any;
  timestamp: number;
  recoverable: boolean;
}

export class MancalaErrorHandler {
  private errorService: ErrorHandlingService;
  private notificationService: NotificationService;
  private errorLog: MancalaError[] = [];
  private readonly MAX_ERROR_LOG = 50;

  constructor(
    errorService: ErrorHandlingService,
    notificationService: NotificationService
  ) {
    this.errorService = errorService;
    this.notificationService = notificationService;
  }

  handleError(
    type: MancalaErrorType,
    message: string,
    details?: any,
    showNotification: boolean = true
  ): MancalaError {
    const error: MancalaError = {
      type,
      message,
      details,
      timestamp: Date.now(),
      recoverable: this.isRecoverable(type),
    };

    this.logError(error);

    const { errorType, severity } = this.classifyError(type);
    this.errorService.handleError(
      new Error(message),
      errorType,
      severity,
      { component: 'Mancala', additionalData: details },
      { showNotification: false }
    );

    if (showNotification) {
      this.showErrorNotification(error);
    }

    return error;
  }

  private classifyError(
    type: MancalaErrorType
  ): { errorType: ErrorType; severity: ErrorSeverity } {
    switch (type) {
      case MancalaErrorType.INVALID_MOVE:
      case MancalaErrorType.NOT_YOUR_TURN:
      case MancalaErrorType.GAME_ENDED:
      case MancalaErrorType.GAME_LOGIC:
      case MancalaErrorType.AI_TIMEOUT:
        return { errorType: ErrorType.GAME_LOGIC, severity: ErrorSeverity.LOW };

      case MancalaErrorType.CONNECTION_ERROR:
      case MancalaErrorType.MULTIPLAYER_VALIDATION:
        return { errorType: ErrorType.MULTIPLAYER, severity: ErrorSeverity.MEDIUM };

      case MancalaErrorType.CORRUPTED_DATA:
      case MancalaErrorType.SAVE_ERROR:
      case MancalaErrorType.LOAD_ERROR:
        return { errorType: ErrorType.STORAGE, severity: ErrorSeverity.MEDIUM };

      default:
        return { errorType: ErrorType.SYSTEM, severity: ErrorSeverity.MEDIUM };
    }
  }

  // Convenience methods
  handleGameLogicError(reason: string): MancalaError {
    return this.handleError(
      MancalaErrorType.GAME_LOGIC,
      `Game error: ${reason}`,
      { reason }
    );
  }

  handleInvalidMove(pitIndex: number): MancalaError {
    return this.handleError(
      MancalaErrorType.INVALID_MOVE,
      `Invalid move: pit ${pitIndex + 1} is empty. Choose a pit with seeds.`,
      { pitIndex }
    );
  }

  handleNotYourTurn(): MancalaError {
    return this.handleError(
      MancalaErrorType.NOT_YOUR_TURN,
      "It's not your turn. Please wait.",
      undefined
    );
  }

  handleConnectionError(error: any): MancalaError {
    return this.handleError(
      MancalaErrorType.CONNECTION_ERROR,
      'Connection lost. Attempting to reconnect...',
      error,
      true
    );
  }

  handleCorruptedData(dataType: string, error: any): MancalaError {
    return this.handleError(
      MancalaErrorType.CORRUPTED_DATA,
      'Unable to load saved game. Starting new game.',
      { dataType, error },
      true
    );
  }

  handleSaveError(gameId: string, error: any): MancalaError {
    return this.handleError(
      MancalaErrorType.SAVE_ERROR,
      'Failed to save game. Your progress may be lost.',
      { gameId, error },
      true
    );
  }

  handleLoadError(gameId: string, error: any): MancalaError {
    return this.handleError(
      MancalaErrorType.LOAD_ERROR,
      'Failed to load game. Starting new game.',
      { gameId, error },
      true
    );
  }

  // Internal helpers
  private isRecoverable(type: MancalaErrorType): boolean {
    switch (type) {
      case MancalaErrorType.INVALID_MOVE:
      case MancalaErrorType.NOT_YOUR_TURN:
      case MancalaErrorType.GAME_ENDED:
      case MancalaErrorType.GAME_LOGIC:
      case MancalaErrorType.CONNECTION_ERROR:
      case MancalaErrorType.CORRUPTED_DATA:
      case MancalaErrorType.AI_TIMEOUT:
      case MancalaErrorType.SAVE_ERROR:
      case MancalaErrorType.LOAD_ERROR:
        return true;
      default:
        return false;
    }
  }

  private logError(error: MancalaError): void {
    this.errorLog.push(error);
    if (this.errorLog.length > this.MAX_ERROR_LOG) {
      this.errorLog.shift();
    }
  }

  private showErrorNotification(error: MancalaError): void {
    const notificationType = error.recoverable
      ? NotificationType.WARNING
      : NotificationType.ERROR;
    this.notificationService.addNotification({
      type: notificationType,
      title: 'Mancala Error',
      message: error.message,
      duration: 5000,
    });
  }

  getErrorLog(): MancalaError[] {
    return [...this.errorLog];
  }

  clearErrorLog(): void {
    this.errorLog = [];
  }
}
